import '@fortawesome/fontawesome-free/css/all.min.css';
import React, {
  useEffect,
  useState,
  useRef,
  useCallback
} from 'react';

import ChatSocketListener from "./ChatSocketListener";
import '../css/ChatWindow.css';
import SubscriptionModal from './SubscriptionModal';


function ChatWindow({
  user,
  callManagerRef,
  inCall,
  setInCall,
  activeCallType,
  setActiveCallType,
  activeCallUser,
  setActiveCallUser
}) {

  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);

  const messagesEndRef = useRef(null);

  const [readBySelectedUser, setReadBySelectedUser] =
    useState(false);


  const [isMuted, setIsMuted] =
    useState(false);

  const [openGiftModal, setOpenGiftModal] =
    useState(false);

  const [selectedGift, setSelectedGift] =
    useState(null);

  const [previewImage, setPreviewImage] =
    useState(null);

  const [showComposerMenu, setShowComposerMenu] =
    useState(false);

  const imageInputRef = useRef(null);

  const [
    showSubscriptionModal,
    setShowSubscriptionModal
  ] = useState(false);


  // =========================================================
  // SCROLL BOTTOM
  // =========================================================

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };


  // =========================================================
  // SAVE SELECTED USER
  // =========================================================

  useEffect(() => {

    if (user) {
      localStorage.setItem(
        "selectedChatUser",
        JSON.stringify(user)
      );
    }

  }, [user]);


  // =========================================================
  // LOAD CURRENT USER
  // =========================================================

  useEffect(() => {

    const userJson =
      localStorage.getItem('currentUser');

    if (userJson) {

      try {

        const parsed =
          JSON.parse(userJson);

        setCurrentUser(parsed);

      } catch (err) {

        console.error(
          "❌ Failed to parse currentUser",
          err
        );
      }
    }

  }, []);


  // =========================================================
  // LOAD CONVERSATION
  // =========================================================

  useEffect(() => {

    if (!user?.id) {
      return;
    }

    fetch(
      `${process.env.REACT_APP_API_URL}/api/chat/conversation/${user.id}`,
      {
        headers: {
          Authorization:
            `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    )
      .then(res => res.json())
      .then(data => {

        setConversation(data);

      })
      .catch(err => {

        console.error(
          "❌ Failed to load messages",
          err
        );
      });

  }, [user]);



  const getSupportedAudioMimeType = () => {

    if (
      typeof MediaRecorder === 'undefined'
    ) {
      return '';
    }

    const types = [
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/webm;codecs=opus',
      'audio/webm'
    ];

    for (const type of types) {

      if (
        MediaRecorder.isTypeSupported?.(type)
      ) {
        return type;
      }

    }

    return '';
  };


  const getAudioExtension = (mimeType = '') => {

    const type =
      mimeType.toLowerCase();

    if (type.includes('mp4')) {
      return 'm4a';
    }

    if (type.includes('webm')) {
      return 'webm';
    }

    if (type.includes('ogg')) {
      return 'ogg';
    }

    return 'audio';
  };


  // =========================================================
  // START RECORDING
  // =========================================================

  const startRecording = async () => {

    try {

      const stream =
        await navigator.mediaDevices
          .getUserMedia({
            audio: true
          });


      const mimeType =
        getSupportedAudioMimeType();


      const recorder =
        mimeType
          ? new MediaRecorder(
            stream,
            { mimeType }
          )
          : new MediaRecorder(stream);


      const chunks = [];


      recorder.ondataavailable = (e) => {

        if (e.data.size > 0) {
          chunks.push(e.data);
        }

      };


      recorder.onstop = () => {

        const finalMimeType =
          recorder.mimeType ||
          chunks[0]?.type ||
          mimeType ||
          'audio/mp4';


        const blob =
          new Blob(
            chunks,
            {
              type: finalMimeType
            }
          );


        setAudioBlob(blob);


        stream
          .getTracks()
          .forEach(
            track => track.stop()
          );

      };


      recorder.start();

      setMediaRecorder(recorder);

      setIsRecording(true);


      console.log(
        '🎤 Recording format:',
        recorder.mimeType
      );

    } catch (error) {

      console.error(
        '🎤 Micro access denied or failed',
        error
      );

    }

  };

  // =========================================================
  // STOP RECORDING
  // =========================================================

  const stopRecording = () => {

    if (mediaRecorder) {

      mediaRecorder.stop();

      setIsRecording(false);
    }
  };


  // =========================================================
  // SEND MESSAGE
  // =========================================================

  const handleSend = async (e) => {

    e.preventDefault();

    const token =
      localStorage.getItem("authToken");

    try {

      // Refresh current user
      const refreshedUserRes =
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );

      if (refreshedUserRes.ok) {

        const refreshedUser =
          await refreshedUserRes.json();

        setCurrentUser(refreshedUser);

        localStorage.setItem(
          "currentUser",
          JSON.stringify(refreshedUser)
        );

        /*
        if (!refreshedUser.hasSubscription) {
            setShowSubscriptionModal(true);
            return;
        }
        */
      }


      // Voice recording
      if (
        isRecording &&
        mediaRecorder
      ) {

        stopRecordingAndSend();
        return;
      }


      if (
        !user ||
        (
          !message.trim() &&
          !image &&
          !audioBlob
        )
      ) {
        return;
      }


      const formData =
        new FormData();

      formData.append(
        "content",
        message
      );


      if (image) {

        formData.append(
          "image",
          image
        );
      }


      if (audioBlob) {

        formData.append(
          "audio",
          audioBlob,
          "voice-message.webm"
        );
      }


      const res =
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/send/${user.id}`,
          {
            method: 'POST',

            headers: {
              Authorization:
                `Bearer ${token}`
            },

            body: formData
          }
        );


      if (!res.ok) {

        throw new Error(
          "Failed to send"
        );
      }


      setMessage('');
      setImage(null);
      setAudioBlob(null);


      const updated =
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/conversation/${user.id}`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );


      const data =
        await updated.json();

      setConversation(data);

      setTimeout(
        scrollToBottom,
        100
      );

    } catch (err) {

      console.error(
        "❌ Send error:",
        err
      );
    }
  };


  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (timestamp) => {

    if (!timestamp) {
      return '';
    }

    const date =
      new Date(timestamp);

    return `${date.toLocaleDateString()} ${date.toLocaleTimeString(
      [],
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    )}`;
  };


  // =========================================================
  // STOP RECORDING AND SEND
  // =========================================================

  const stopRecordingAndSend = () => {

    if (
      !mediaRecorder ||
      !user?.id
    ) {
      return;
    }


    const chunks = [];


    mediaRecorder.ondataavailable = (e) => {

      if (e.data.size > 0) {
        chunks.push(e.data);
      }

    };


    mediaRecorder.onstop = async () => {

      const mimeType =
        mediaRecorder.mimeType ||
        chunks[0]?.type ||
        'audio/mp4';


      const blob =
        new Blob(
          chunks,
          {
            type: mimeType
          }
        );


      const extension =
        getAudioExtension(
          mimeType
        );


      console.log(
        '🎤 Sending audio:',
        mimeType,
        extension,
        blob.size
      );


      const formData =
        new FormData();


      formData.append(
        'content',
        ''
      );


      formData.append(
        'audio',
        blob,
        `voice-message.${extension}`
      );


      try {

        const res =
          await fetch(
            `${process.env.REACT_APP_API_URL}/api/chat/send/${user.id}`,
            {
              method: 'POST',

              headers: {
                Authorization:
                  `Bearer ${localStorage.getItem(
                    'authToken'
                  )}`
              },

              body: formData
            }
          );


        if (!res.ok) {
          throw new Error(
            'Failed to send'
          );
        }


        setMessage('');

        setImage(null);

        setAudioBlob(null);

        setIsRecording(false);


        const updated =
          await fetch(
            `${process.env.REACT_APP_API_URL}/api/chat/conversation/${user.id}`,
            {
              headers: {
                Authorization:
                  `Bearer ${localStorage.getItem(
                    'authToken'
                  )}`
              }
            }
          );


        const data =
          await updated.json();


        setConversation(data);


        setTimeout(
          scrollToBottom,
          100
        );


      } catch (err) {

        console.error(
          '❌ Audio send error:',
          err
        );

      }

    };


    mediaRecorder.stop();

  };


  // =========================================================
  // MESSAGE READ
  // =========================================================

  const handleRead =
    useCallback(() => {

      if (!user?.id) {
        return;
      }

      fetch(
        `${process.env.REACT_APP_API_URL}/api/chat/mark-as-read/${user.id}`,
        {
          method: 'PUT',

          headers: {
            Authorization:
              `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      )
        .then(() => {

          setReadBySelectedUser(true);

        })
        .catch(error => {

          console.error(
            "❌ Mark as read failed:",
            error
          );
        });

    }, [user?.id]);


  // =========================================================
  // SCROLL ON NEW MESSAGE
  // =========================================================

  useEffect(() => {

    scrollToBottom();

  }, [conversation]);


  // =========================================================
  // MARK CURRENT CONVERSATION READ
  // =========================================================

  useEffect(() => {

    if (
      !user ||
      !currentUser
    ) {
      return;
    }


    if (
      user.id === currentUser.id
    ) {
      return;
    }


    fetch(
      `${process.env.REACT_APP_API_URL}/api/chat/mark-as-read/${user.id}`,
      {
        method: 'PUT',

        headers: {
          Authorization:
            `Bearer ${localStorage.getItem('authToken')}`
        }
      }
    )
      .then(() => {

        window.dispatchEvent(
          new Event(
            "refresh-notifications"
          )
        );

        window.dispatchEvent(
          new Event(
            "refresh-contacts"
          )
        );


        return fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/conversation/${user.id}`,
          {
            headers: {
              Authorization:
                `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );
      })
      .then(res => res.json())
      .then(data => {

        setConversation(data);

      })
      .catch(error => {

        console.error(
          "❌ Conversation refresh failed:",
          error
        );
      });

  }, [user, currentUser]);


  useEffect(() => {

    setReadBySelectedUser(false);

  }, [user?.id]);


  // =========================================================
  // START AUDIO / VIDEO CALL
  // =========================================================

  const handleStartCall = async (type) => {

    if (!user?.id) {

      console.warn(
        "⚠️ No selected user"
      );

      return;
    }


    const token =
      localStorage.getItem(
        "authToken"
      );


    try {

      const refreshedUserRes =
        await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: {
              Authorization:
                `Bearer ${token}`
            }
          }
        );


      if (refreshedUserRes.ok) {

        const refreshedUser =
          await refreshedUserRes.json();

        setCurrentUser(
          refreshedUser
        );

        localStorage.setItem(
          "currentUser",
          JSON.stringify(
            refreshedUser
          )
        );


        /*
        if (!refreshedUser.hasSubscription) {
            setShowSubscriptionModal(true);
            return;
        }
        */
      }


      if (
        inCall ||
        callManagerRef.current?.isInCall()
      ) {

        console.log(
          "⚠️ Already in a call"
        );

        return;
      }


      console.log(
        `Starting ${type} call`
      );

      setActiveCallUser(user);
      setActiveCallType(type);


      /*
       * IMPORTANT
       *
       * First render the call UI so that:
       *
       * localVideo
       * remoteVideo
       * remoteAudio
       *
       * exist in the DOM.
       */
      setInCall(true);


      // Let React render call elements first
      await new Promise(
        resolve =>
          setTimeout(
            resolve,
            50
          )
      );


      await callManagerRef.current?.startCall(
        type
      );

    } catch (error) {

      console.error(
        "❌ Error starting call:",
        error
      );

      setInCall(false);
      setActiveCallType(null);
    }
  };


  // =========================================================
  // END CALL
  // =========================================================

  const handleEndCall = async () => {

    console.log(
      "📴 Ending call"
    );


    try {

      await callManagerRef.current?.endCall();

    } catch (error) {

      console.error(
        "❌ End call error:",
        error
      );
    }


    setInCall(false);
    setActiveCallType(null);
    setActiveCallUser(null);

    setIsMuted(false);
  };


  // =========================================================
  // MUTE MICROPHONE
  // =========================================================

  const toggleMute = () => {

    const microphoneEnabled =
      callManagerRef.current?.toggleMute();


    if (
      typeof microphoneEnabled ===
      "boolean"
    ) {

      setIsMuted(
        !microphoneEnabled
      );
    }
  };


  // =========================================================
  // GIFTS
  // =========================================================

  const handleOfferGift = (
    receiverId
  ) => {

    localStorage.setItem(
      "selectedUserId",
      receiverId
    );

    window.location.href =
      "/gifts";
  };


  // =========================================================
  // SUBSCRIPTION RETURN
  // =========================================================

  useEffect(() => {

    const query =
      new URLSearchParams(
        window.location.search
      );

    const subscribed =
      query.get("subscribed");

    const plan =
      query.get("plan");

    const chatUserId =
      query.get("userId");


    if (
      subscribed &&
      plan &&
      chatUserId
    ) {

      const token =
        localStorage.getItem(
          "authToken"
        );


      fetch(
        `${process.env.REACT_APP_API_URL}/api/subscription/confirm?plan=${plan}`,
        {
          method: "POST",

          headers: {
            Authorization:
              `Bearer ${token}`
          }
        }
      )
        .then(res => {

          if (!res.ok) {

            throw new Error(
              "Échec de la confirmation de l'abonnement"
            );
          }

          return res.text();
        })

        .then(() => {

          return fetch(
            `${process.env.REACT_APP_API_URL}/api/users/me`,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );
        })

        .then(res => res.json())

        .then(data => {

          localStorage.setItem(
            "currentUser",
            JSON.stringify(data)
          );

          setCurrentUser(data);


          const stored =
            localStorage.getItem(
              "selectedChatUser"
            );


          if (stored) {

            const parsed =
              JSON.parse(stored);

            window.location.href =
              `/chat?userId=${parsed.id}`;

          } else {

            window.location.href =
              "/chat";
          }
        })

        .catch(err => {

          console.error(
            "❌ Confirmation abonnement échouée :",
            err
          );
        });
    }

  }, []);

  const callUser =
    activeCallUser || user;


  // Image de la personne en appel
  const callProfileImage =
    Array.isArray(callUser?.profilePicture)
      ? callUser.profilePicture[0]
      : callUser?.profilePicture ||
      callUser?.mediaUrls?.[0] ||
      callUser?.image ||
      null;


  // Image de la conversation actuellement ouverte
  const profileImage =
    Array.isArray(user?.profilePicture)
      ? user.profilePicture[0]
      : user?.profilePicture ||
      user?.mediaUrls?.[0] ||
      user?.image ||
      null;


  // =========================================================
  // RENDER
  // =========================================================

  return (

    <div className="chat-window">


      {/* =================================================
                CHAT WEBSOCKET
            ================================================= */}

      {currentUser?.id && user?.id && (

        <ChatSocketListener

          currentUser={
            currentUser
          }

          selectedUserId={
            user.id
          }

          onMessageReceived={(
            newMsg
          ) => {

            setConversation(
              prev => {

                const alreadyExists =
                  prev.some(
                    m =>
                      m.uuid ===
                      newMsg.uuid
                      ||
                      (
                        m.content ===
                        newMsg.content
                        &&
                        m.senderId ===
                        newMsg.senderId
                        &&
                        new Date(
                          m.timestamp
                        ).getTime()
                        ===
                        new Date(
                          newMsg.timestamp
                        ).getTime()
                      )
                  );


                return alreadyExists
                  ? prev
                  : [
                    ...prev,
                    newMsg
                  ];
              }
            );


            setTimeout(
              scrollToBottom,
              100
            );


            window.dispatchEvent(
              new Event(
                "refresh-notifications"
              )
            );


            window.dispatchEvent(
              new Event(
                "refresh-contacts"
              )
            );
          }}

          onMessageRead={
            handleRead
          }
        />
      )}


      {/* =================================================
                CALL UI
            ================================================= */}

      {inCall && (
        <>
          {activeCallType === 'video' ? (

            // 📹 VIDEO
            <div className="call-container">

              <div className="video-container">
                <video
                  id="remoteVideo"
                  autoPlay
                  playsInline
                  className="remote-video"
                />

                <video
                  id="localVideo"
                  autoPlay
                  muted
                  playsInline
                  className="local-video"
                />
              </div>

              <div className="call-controls">

                <button
                  onClick={toggleMute}
                  className={`mute-button ${isMuted ? 'muted' : ''}`}
                >
                  <i
                    className={`fas ${isMuted
                      ? 'fa-microphone-slash'
                      : 'fa-microphone'
                      }`}
                  />
                </button>

                <button
                  onClick={handleEndCall}
                  className="end-call-button"
                >
                  <i className="fas fa-phone-slash" />
                </button>

              </div>

            </div>

          ) : (

            // 📞 AUDIO
            <div className="call-container audio-call-container">

              <div className="audio-call-profile">

                <div className="audio-avatar-wrapper">

                  {callProfileImage ? (

                    <img
                      src={callProfileImage}
                      alt={callUser?.name || "User"}
                      className="audio-avatar"
                    />

                  ) : (

                    <div className="audio-avatar-fallback">
                      {callUser?.name
                        ?.charAt(0)
                        ?.toUpperCase() || "?"}
                    </div>

                  )}
                </div>
                <h2 className="audio-call-name">
                  {callUser?.name || "User"}
                </h2>

                <p className="audio-call-status">
                  Appel audio en cours
                </p>

                <div className="audio-wave">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>

              </div>

              {/* On réutilise tes boutons existants */}
              <div className="call-controls audio-call-controls">

                <button
                  onClick={toggleMute}
                  className={`mute-button ${isMuted ? 'muted' : ''}`}
                >
                  <i
                    className={`fas ${isMuted
                      ? 'fa-microphone-slash'
                      : 'fa-microphone'
                      }`}
                  />
                </button>

                <button
                  onClick={handleEndCall}
                  className="end-call-button"
                >
                  <i className="fas fa-phone-slash" />
                </button>

              </div>

            </div>
          )}

          <audio
            id="remoteAudio"
            autoPlay
            playsInline
            style={{ display: 'none' }}
          />
        </>
      )}

      {/*
                IMPORTANT

                This is where remote WebRTC
                AUDIO will be played.

                Do NOT remove it.
            */}

      <audio

        id="remoteAudio"

        autoPlay

        playsInline="true"

        style={{
          display: 'none'
        }}

      />


      {/* =================================================
                HEADER
            ================================================= */}

      <div className="chat-header">

        <div className="chat-header-user">

          <div className="chat-header-avatar">

            {profileImage ? (
              <img
                src={profileImage}
                alt={user?.name || 'User'}
              />
            ) : (
              <span>
                {user?.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            )}

          </div>

          <div className="chat-header-user-info">

            <strong>
              {user?.name || 'Conversation'}
            </strong>

            <span>
              {user?.online
                ? 'Online'
                : 'Conversation'}
            </span>

          </div>

        </div>


        {user && (
          <div className="chat-header-actions">

            {/* GIFT */}

            <button
              type="button"
              className="chat-header-action gift chat-header-gift-desktop"
              onClick={(e) => {
                e.stopPropagation();
                handleOfferGift(user.id);
              }}
              title="Offrir un cadeau"
              aria-label="Offrir un cadeau"
            >
              <i className="fas fa-gift" />
            </button>


            {/* AUDIO CALL */}

            {!inCall && (
              <button
                type="button"
                className="chat-header-action audio"
                onClick={() =>
                  handleStartCall('audio')
                }
                title="Appel vocal"
                aria-label="Appel vocal"
              >
                <i className="fas fa-phone" />
              </button>
            )}


            {/* VIDEO CALL */}

            {!inCall && (
              <button
                type="button"
                className="chat-header-action video"
                onClick={() =>
                  handleStartCall('video')
                }
                title="Appel vidéo"
                aria-label="Appel vidéo"
              >
                <i className="fas fa-video" />
              </button>
            )}

          </div>
        )}

      </div>


      {/* =================================================
                MESSAGES
            ================================================= */}

      <div className="chat-messages">


        {conversation.map(
          (msg, idx) => (

            <div

              key={
                msg.uuid ||
                msg.id ||
                idx
              }

              className={
                `message ${msg.senderId === currentUser?.id
                  ? 'sent'
                  : 'received'
                } ${msg.audioUrl
                  ? 'audio-message-bubble'
                  : ''
                }`
              }
            >


              {(() => {


                // =============================
                // GIFT MESSAGE
                // =============================

                if (
                  msg.content?.startsWith(
                    "gift:"
                  )
                ) {

                  const giftId =
                    parseInt(
                      msg.content.split(
                        ":"
                      )[1]
                    );


                  const giftMap = {

                    1: {
                      image:
                        "bouquet-tulipes.png",
                      desc:
                        "🌷 Bouquet de tulipes"
                    },

                    2: {
                      image:
                        "elephants.png",
                      desc:
                        "🐘 Éléphant en peluche"
                    },

                    3: {
                      image:
                        "peluche-coeur.png",
                      desc:
                        "❤️ Peluche cœur géant"
                    },

                    4: {
                      image:
                        "bracele.png",
                      desc:
                        "💎 Bracelet en or"
                    },

                    5: {
                      image:
                        "lien-symbolique.png",
                      desc:
                        "🔗 Lien symbolique"
                    },

                    6: {
                      image:
                        "chocolate.png",
                      desc:
                        "🍫 Chocolats"
                    },

                    7: {
                      image:
                        "fleur.png",
                      desc:
                        "🌹 Fleur sous cloche"
                    },

                    8: {
                      image:
                        "bougie.png",
                      desc:
                        "🕯️ Bougie parfumée"
                    }
                  };


                  const gift =
                    giftMap[
                    giftId
                    ];


                  if (!gift) {

                    return (
                      <div>
                        🎁 Cadeau
                      </div>
                    );
                  }


                  return (

                    <div

                      className="gift-message"

                      onClick={() => {

                        setSelectedGift(
                          gift
                        );

                        setOpenGiftModal(
                          true
                        );
                      }}

                      style={{
                        cursor:
                          "pointer"
                      }}
                    >

                      <img

                        src={
                          `/gifts/${gift.image}`
                        }

                        alt="cadeau"
                      />

                      <p>

                        🎁 {gift.desc}

                      </p>

                    </div>
                  );
                }


                // =============================
                // NORMAL TEXT
                // =============================

                if (
                  msg.content
                ) {

                  return (
                    <div>
                      {msg.content}
                    </div>
                  );
                }


                return null;

              })()}


              {/* IMAGE MESSAGE */}

              {/* IMAGE MESSAGE */}

              {msg.imageUrl && (
                <img
                  src={msg.imageUrl}
                  alt="Message"
                  className="chat-message-image"
                  onClick={() =>
                    setPreviewImage(msg.imageUrl)
                  }
                />
              )}


              {/* AUDIO MESSAGE */}

              {msg.audioUrl && (
                <audio
                  controls
                  preload="auto"
                  playsInline
                  src={msg.audioUrl}
                  className="chat-audio-message"

                  onLoadedMetadata={(event) => {
                    const audio =
                      event.currentTarget;

                    audio.defaultMuted = false;
                    audio.muted = false;

                    try {
                      audio.volume = 1;
                    } catch (error) {
                      // Some mobile browsers manage volume themselves
                    }
                  }}

                  onCanPlay={(event) => {
                    const audio =
                      event.currentTarget;

                    audio.defaultMuted = false;
                    audio.muted = false;
                  }}

                  onPlay={(event) => {
                    const audio =
                      event.currentTarget;

                    audio.defaultMuted = false;
                    audio.muted = false;

                    try {
                      audio.volume = 1;
                    } catch (error) {
                      // Ignore mobile volume restrictions
                    }

                    console.log(
                      '🔊 Received audio playing:',
                      {
                        url: msg.audioUrl,
                        muted: audio.muted,
                        volume: audio.volume,
                        readyState: audio.readyState,
                        duration: audio.duration
                      }
                    );
                  }}
                >
                  Your browser does not support
                  the audio element.
                </audio>
              )}


              {/* MESSAGE META */}

              <div className="meta-info">


                <span>

                  {formatDate(
                    msg.timestamp
                  )}

                </span>


                {msg.senderId ===
                  currentUser?.id && (

                    <span className="message-status">


                      {(
                        msg.isRead ||
                        readBySelectedUser
                      )
                        ? (

                          <i

                            className="fa fa-check-double"

                            title="Lu"

                            style={{
                              color:
                                '#4fc3f7'
                            }}

                          ></i>

                        )
                        : (

                          <i

                            className="fa fa-check"

                            title="Envoyé"

                            style={{
                              color:
                                '#aaa'
                            }}

                          ></i>
                        )
                      }

                    </span>
                  )}

              </div>

            </div>
          )
        )}


        <div ref={messagesEndRef}></div>

      </div>


      {/* =================================================
                NO USER SELECTED
            ================================================= */}

      {!user && (

        <div

          className="chat-placeholder text-center"

          style={{
            padding:
              '20px',

            color:
              '#999'
          }}
        >

          👉 Sélectionnez un contact pour démarrer une conversation

        </div>
      )}


      {/* =================================================
                MESSAGE INPUT
            ================================================= */}

      {user && (

        <div className="chat-input-container">


          <div className="input-wrapper">


            {/* MICROPHONE */}

            <i

              className="fa fa-microphone mic-icon"

              onClick={
                isRecording
                  ? stopRecording
                  : startRecording
              }

            ></i>


            {!isRecording
              ? (

                <>


                  {/* TEXT */}

                  <input

                    type="text"

                    value={
                      message
                    }

                    placeholder="Type a message..."

                    onChange={(e) => {

                      setMessage(
                        e.target.value
                      );
                    }}

                    onKeyDown={(e) => {

                      if (
                        e.key ===
                        "Enter"
                      ) {

                        handleSend(
                          e
                        );
                      }
                    }}

                  />


                  {/* IMAGE */}

                  <label className="camera-label">


                    <i className="fa fa-camera camera-icon" />


                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {

                        setImage(
                          e.target.files?.[0] ||
                          null
                        );

                        setShowComposerMenu(false);
                      }}
                      hidden
                    />

                  </label>


                  {/* IMAGE NAME */}

                  {image && (

                    <span className="image-indicator">

                      {
                        image.name.length >
                          15
                          ? image.name.slice(
                            0,
                            12
                          ) + '...'
                          : image.name
                      }

                    </span>
                  )}

                  {/* MORE OPTIONS */}

                  <button
                    type="button"
                    className={`chat-composer-more ${showComposerMenu
                      ? 'is-open'
                      : ''
                      }`}
                    onClick={() =>
                      setShowComposerMenu(
                        previous => !previous
                      )
                    }
                    aria-label="More options"
                  >
                    <i className="fas fa-plus" />
                  </button>

                </>

              )
              : (

                <div className="recording-indicator">


                  <i className="fa fa-circle pulse-icon"></i>


                  <span>

                    Recording... Tap mic to stop

                  </span>

                </div>
              )
            }

          </div>

          {showComposerMenu && (

            <div className="chat-composer-menu">

              {/* PHOTO */}

              <button
                type="button"
                className="chat-composer-menu-item"
                onClick={() => {

                  imageInputRef.current
                    ?.click();

                }}
              >

                <span className="chat-composer-menu-icon photo">
                  <i className="fas fa-camera" />
                </span>

                <span>
                  <strong>
                    Photo
                  </strong>

                  <small>
                    Send a picture
                  </small>
                </span>

              </button>


              {/* GIFT */}

              <button
                type="button"
                className="chat-composer-menu-item"
                onClick={() => {

                  setShowComposerMenu(false);

                  handleOfferGift(
                    user.id
                  );

                }}
              >

                <span className="chat-composer-menu-icon gift">
                  <i className="fas fa-gift" />
                </span>

                <span>
                  <strong>
                    Gift
                  </strong>

                  <small>
                    Send a special gift
                  </small>
                </span>

              </button>

            </div>

          )}

          {/* SEND BUTTON */}

          <button
            onClick={
              handleSend
            }
          >

            <i

              className="fa fa-paper-plane"

              style={{
                color:
                  "white"
              }}

            ></i>

          </button>

        </div>
      )}


      {/* =================================================
                IMPORTANT:
                NO CALLMANAGER HERE
                NO CALLMODAL HERE
            ================================================= */}


      {/* =================================================
                GIFT POPUP
            ================================================= */}

      {
        openGiftModal &&
        selectedGift &&
        (

          <div

            className="gift-popup-overlay"

            onClick={() =>
              setOpenGiftModal(
                false
              )
            }
          >

            <div

              className="gift-popup"

              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <img

                src={
                  `/gifts/${selectedGift.image}`
                }

                alt="gift"

              />


              <h3>

                {selectedGift.desc}

              </h3>


              <p className="gift-message-text">

                Ce cadeau symbolise un moment spécial entre vous 💖

              </p>


              <button

                onClick={() =>
                  setOpenGiftModal(
                    false
                  )
                }
              >

                Fermer

              </button>

            </div>

          </div>
        )
      }


      {/* =================================================
    IMAGE FULLSCREEN PREVIEW
================================================= */}

      {previewImage && (
        <div
          className="chat-image-preview-overlay"
          onClick={() =>
            setPreviewImage(null)
          }
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
        >

          <button
            type="button"
            className="chat-image-preview-close"
            onClick={() =>
              setPreviewImage(null)
            }
            aria-label="Close image"
          >
            <i className="fas fa-times" />
          </button>


          <img
            src={previewImage}
            alt="Preview"
            className="chat-image-preview-full"
            onClick={(event) =>
              event.stopPropagation()
            }
          />

        </div>
      )}


      {/* =================================================
                SUBSCRIPTION
            ================================================= */}

      <SubscriptionModal

        isOpen={
          showSubscriptionModal
        }

        onClose={() =>
          setShowSubscriptionModal(
            false
          )
        }

      />

    </div>
  );
}


export default ChatWindow;